/**
 * The Organizations page's dialogs (Story 48, task 208): a new MSP with its
 * first Admin, rename, move, resale, and the cross-MSP indicator switch.
 *
 * The API enforces every rule here (ADR-0033): a move stays inside the
 * caller's subtree and never beneath itself (409 `cycle`), resale stays
 * within the parent's (409 `not_resellable`) and is not narrowed while a
 * Client below still uses it (409 `in_use`, naming them). Its 409s are
 * shown inline; the forms only avoid offering what it is certain to refuse.
 */

import { useState } from 'react';

import type { CreateOrgBody, OrgNode } from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ErrorNote, INPUT, ModalShell } from '@bluesphere/mdr-alert-panel';
import { orgLabel, serviceLabel } from '../lib/orgs';

const LABEL = 'text-xs uppercase tracking-wider text-bluesphere-textSecondary';

function Header({ title, body }: { title: string; body?: string }) {
  return (
    <div className="border-b border-bluesphere-border px-6 py-5">
      <h2 className="text-lg font-semibold text-bluesphere-textOnLight">{title}</h2>
      {body && <p className="mt-1 text-sm text-bluesphere-textSecondary">{body}</p>}
    </div>
  );
}

function Footer({
  onCancel,
  submitLabel,
  pendingLabel,
  isPending,
  canSubmit,
}: {
  onCancel: () => void;
  submitLabel: string;
  pendingLabel: string;
  isPending: boolean;
  canSubmit: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
        Cancel
      </PillButton>
      <PillButton size="sm" type="submit" disabled={!canSubmit || isPending}>
        {isPending ? pendingLabel : submitLabel}
      </PillButton>
    </div>
  );
}

interface ModalProps {
  onCancel: () => void;
  isPending: boolean;
  error: unknown;
}

function ServiceChecks({
  allowed,
  value,
  onChange,
}: {
  allowed: string[];
  value: string[];
  onChange: (services: string[]) => void;
}) {
  if (allowed.length === 0) {
    return (
      <p className="text-xs text-bluesphere-muted">
        The organization above resells nothing yet, so there is nothing to offer here.
      </p>
    );
  }
  return (
    <div className="space-y-1.5">
      {allowed.map((key) => (
        <label
          key={key}
          className="flex cursor-pointer items-center gap-2 text-sm text-bluesphere-textOnLight"
        >
          <input
            type="checkbox"
            checked={value.includes(key)}
            onChange={(e) =>
              onChange(e.target.checked ? [...value, key] : value.filter((v) => v !== key))
            }
            className="h-4 w-4 rounded-sm border-bluesphere-borderMuted"
          />
          {serviceLabel(key)}
        </label>
      ))}
    </div>
  );
}

// ----- New MSP ----------------------------------------------------------------

export function NewMspModal({
  parent,
  onSubmit,
  onCancel,
  isPending,
  error,
}: ModalProps & { parent: OrgNode; onSubmit: (body: CreateOrgBody) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [resellable, setResellable] = useState<string[]>([]);
  const canSubmit = name.trim().length > 0 && /^[^@\s]+@[^@\s]+$/.test(email.trim());

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <Header
        title={`New MSP under ${orgLabel(parent)}`}
        body="An MSP is created with its first Admin, who manages its users, Clients and settings. The record binds the first time that account signs in with Microsoft."
      />
      <form
        className="space-y-4 px-6 py-5"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          const display = adminName.trim();
          onSubmit({
            name: name.trim(),
            parent_id: parent.org_id,
            first_admin: { email: email.trim(), ...(display ? { display_name: display } : {}) },
            resellable,
          });
        }}
      >
        <label className="block">
          <span className={LABEL}>MSP name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Managed Services"
            className={INPUT}
          />
        </label>
        <label className="block">
          <span className={LABEL}>First Admin's email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={INPUT}
          />
        </label>
        <label className="block">
          <span className={LABEL}>First Admin's name (optional)</span>
          <input
            value={adminName}
            onChange={(e) => setAdminName(e.target.value)}
            className={INPUT}
          />
        </label>
        <fieldset className="space-y-2">
          <legend className={LABEL}>Services it may resell</legend>
          <ServiceChecks
            allowed={parent.resellable ?? []}
            value={resellable}
            onChange={setResellable}
          />
          <p className="text-xs text-bluesphere-muted">
            Its Clients can only be engaged for these. A new Client starts with MDR.
          </p>
        </fieldset>
        <ErrorNote error={error} />
        <Footer
          onCancel={onCancel}
          submitLabel="Create MSP"
          pendingLabel="Creating…"
          isPending={isPending}
          canSubmit={canSubmit}
        />
      </form>
    </ModalShell>
  );
}

// ----- Rename -----------------------------------------------------------------

export function RenameOrgModal({
  node,
  onSubmit,
  onCancel,
  isPending,
  error,
}: ModalProps & { node: OrgNode; onSubmit: (name: string) => void }) {
  const [name, setName] = useState(node.name ?? '');
  const trimmed = name.trim();
  return (
    <ModalShell onClose={onCancel}>
      <Header title={`Rename ${orgLabel(node)}`} />
      <form
        className="space-y-4 px-6 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed && trimmed !== node.name) onSubmit(trimmed);
        }}
      >
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
        <ErrorNote error={error} />
        <Footer
          onCancel={onCancel}
          submitLabel="Rename"
          pendingLabel="Saving…"
          isPending={isPending}
          canSubmit={Boolean(trimmed) && trimmed !== node.name}
        />
      </form>
    </ModalShell>
  );
}

// ----- Move -------------------------------------------------------------------

export function MoveOrgModal({
  label,
  currentParentId,
  candidates,
  onSubmit,
  onCancel,
  isPending,
  error,
}: ModalProps & {
  /** What is moving: an MSP's or a Client's name. */
  label: string;
  currentParentId: string | null;
  /** The parents the caller may move it under, already excluding its own subtree. */
  candidates: { node: OrgNode; depth: number }[];
  onSubmit: (parentId: string) => void;
}) {
  const [parentId, setParentId] = useState('');
  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <Header
        title={`Move ${label}`}
        body="Everything beneath it moves too. Who can see it changes within a minute: Admins of the new parent and above gain it, the old parent's lose it."
      />
      <form
        className="space-y-4 px-6 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (parentId) onSubmit(parentId);
        }}
      >
        <label className="block">
          <span className={LABEL}>New parent</span>
          <select
            autoFocus
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            className={INPUT}
          >
            <option value="">Choose an organization…</option>
            {candidates.map(({ node, depth }) => (
              <option
                key={node.org_id}
                value={node.org_id}
                disabled={node.org_id === currentParentId}
              >
                {'  '.repeat(depth)}
                {orgLabel(node)}
                {node.org_id === currentParentId ? ' (current)' : ''}
              </option>
            ))}
          </select>
        </label>
        <ErrorNote error={error} />
        <Footer
          onCancel={onCancel}
          submitLabel="Move"
          pendingLabel="Moving…"
          isPending={isPending}
          canSubmit={Boolean(parentId) && parentId !== currentParentId}
        />
      </form>
    </ModalShell>
  );
}

// ----- Resale -----------------------------------------------------------------

export function ResaleModal({
  node,
  allowed,
  onSubmit,
  onCancel,
  isPending,
  error,
}: ModalProps & { node: OrgNode; allowed: string[]; onSubmit: (services: string[]) => void }) {
  const [value, setValue] = useState<string[]>(node.resellable ?? []);
  const changed =
    JSON.stringify([...value].sort()) !== JSON.stringify([...(node.resellable ?? [])].sort());
  return (
    <ModalShell onClose={onCancel}>
      <Header
        title={`Services ${orgLabel(node)} may resell`}
        body="Within what the organization above resells. A service still engaged by a Client beneath it cannot be removed until that Client stops using it."
      />
      <form
        className="space-y-4 px-6 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (changed) onSubmit(value);
        }}
      >
        <ServiceChecks allowed={allowed} value={value} onChange={setValue} />
        <ErrorNote error={error} />
        <Footer
          onCancel={onCancel}
          submitLabel="Save"
          pendingLabel="Saving…"
          isPending={isPending}
          canSubmit={changed}
        />
      </form>
    </ModalShell>
  );
}

// ----- Indicators from other MSPs (open question 17) ------------------------------

type IndicatorChoice = 'inherit' | 'on' | 'off';

export function IndicatorsModal({
  node,
  onSubmit,
  onCancel,
  isPending,
  error,
}: ModalProps & { node: OrgNode; onSubmit: (value: boolean | null) => void }) {
  const initial: IndicatorChoice =
    node.consume_across_msps == null ? 'inherit' : node.consume_across_msps ? 'on' : 'off';
  const [choice, setChoice] = useState<IndicatorChoice>(initial);
  const options: { value: IndicatorChoice; label: string; hint: string }[] = [
    {
      value: 'inherit',
      label: 'Follow the organization above',
      hint: `Currently ${node.consumes_across_msps ? 'on' : 'off'}.`,
    },
    {
      value: 'on',
      label: 'On',
      hint: "Sightings at other MSPs' Clients weigh this MSP's detections.",
    },
    { value: 'off', label: 'Off', hint: "Only this MSP's own Clients' sightings count." },
  ];
  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <Header
        title="Use indicators from other MSPs"
        body={`Whether an indicator seen at another MSP's Client raises the score of a matching detection under ${orgLabel(node)}. Nothing about another MSP's Clients is ever shown — only the weight.`}
      />
      <form
        className="space-y-4 px-6 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (choice !== initial) onSubmit(choice === 'inherit' ? null : choice === 'on');
        }}
      >
        <fieldset className="space-y-2">
          {options.map((o) => (
            <label key={o.value} className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio"
                name="indicators"
                checked={choice === o.value}
                onChange={() => setChoice(o.value)}
                className="mt-1"
              />
              <span>
                <span className="text-bluesphere-textOnLight">{o.label}</span>
                <span className="block text-xs text-bluesphere-muted">{o.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <ErrorNote error={error} />
        <Footer
          onCancel={onCancel}
          submitLabel="Save"
          pendingLabel="Saving…"
          isPending={isPending}
          canSubmit={choice !== initial}
        />
      </form>
    </ModalShell>
  );
}
